import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  StickyNote2 as NotesIcon,
  Search as SearchIcon,
  CreditCardOff as CreditCardOffIcon,
  PeopleAlt as PeopleIcon
} from '@mui/icons-material';

export default function UnpaidBills({ bills = [], loading, error, onUpdate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Filter bills based on search query
  const filteredBills = bills.filter(bill => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const invoiceNumber = bill.invoiceNumber || bill._id?.slice(-8)?.toUpperCase() || '';
    const customerName = bill.customer?.name || bill.customer || 'Walk-in Customer';
    const notes = bill.notes || '';
    
    return (
      invoiceNumber.toLowerCase().includes(query) ||
      customerName.toLowerCase().includes(query) ||
      notes.toLowerCase().includes(query)
    );
  });

  // Calculate totals
  const totalUnpaid = filteredBills.reduce((sum, bill) => sum + (bill.total || 0), 0);
  const billCount = filteredBills.length;

  // Pagination logic
  const totalPages = Math.ceil(filteredBills.length / pageSize) || 1;
  const pagedBills = filteredBills.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search or pageSize changes
  useEffect(() => { setPage(1); }, [searchQuery, pageSize]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5' }}>
      {/* Left Panel - Bills List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'white',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CreditCardOffIcon sx={{ fontSize: 32, color: 'error.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Unpaid Bills
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {billCount} {billCount === 1 ? 'bill' : 'bills'} pending payment
              </Typography>
            </Box>
          </Box>
          
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Bills"
              placeholder="Search by invoice, customer, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Show</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Show"
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
          <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'error.lighter' }}>
              <Typography color="error.main" variant="h6" fontWeight={600}>
                Error loading bills
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {error}
              </Typography>
            </Card>
          ) : bills.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center', boxShadow: 0, bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light' }}>
              <Typography color="success.dark" variant="h6" fontWeight={600}>
                ✓ All bills are paid!
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                No pending bills at the moment
              </Typography>
            </Card>
          ) : filteredBills.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary" variant="h6">
                No bills found matching "{searchQuery}"
              </Typography>
              <Button 
                onClick={() => setSearchQuery('')}
                sx={{ mt: 2 }}
                variant="outlined"
              >
                Clear Search
              </Button>
            </Card>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedBills.map((bill) => (
                    <TableRow 
                      key={bill._id}
                      sx={{ 
                        '&:hover': { bgcolor: 'grey.50' },
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={bill.invoiceNumber || `#${bill._id?.slice(-8)?.toUpperCase() || 'N/A'}`}
                          size="small"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            fontSize: '0.813rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {bill.customer?.name || bill.customer || 'Walk-in Customer'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(bill.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                          label={`${bill.products?.length || 0} items`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        {bill.notes ? (
                          <Chip
                            icon={<NotesIcon sx={{ fontSize: 16 }} />}
                            label="Notes"
                            size="small"
                            color="info"
                            sx={{ fontSize: '0.75rem' }}
                            title={bill.notes}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight={700} color="error.main">
                          {formatCurrency(bill.total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onUpdate?.(bill)}
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2
                          }}
                        >
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Box>
        </Box>

        {/* Pagination */}
        {!loading && !error && filteredBills.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Right Panel - Summary */}
      <Box sx={{ 
        width: 400, 
        bgcolor: 'background.paper', 
        borderLeft: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Summary Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.main', 
          color: 'white' 
        }}>
          <Typography variant="subtitle2" fontWeight={600}>
            SUMMARY
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Total Unpaid Amount */}
            <Card sx={{ boxShadow: 2, border: '2px solid', borderColor: 'error.main' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'error.lighter',
                    color: 'error.main'
                  }}>
                    <MoneyIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Total Unpaid
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="error.main">
                  {formatCurrency(totalUnpaid)}
                </Typography>
              </CardContent>
            </Card>

            {/* Bills Count */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'warning.lighter',
                    color: 'warning.main'
                  }}>
                    <ReceiptIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Pending Bills
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {billCount}
                </Typography>
              </CardContent>
            </Card>

            {/* Unique Customers */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'info.lighter',
                    color: 'info.main'
                  }}>
                    <PeopleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Unique Customers
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {new Set(filteredBills.map(b => b.customer?.name || b.customer || 'Walk-in')).size}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Info Box */}
          <Card sx={{ bgcolor: 'info.lighter', boxShadow: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="info.dark" fontWeight={500} gutterBottom>
                💡 Quick Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Click "Update" to mark bills as paid
                </Typography>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Use search to filter by invoice or customer
                </Typography>
                <Typography component="li" variant="caption">
                  Bills with notes are marked with a badge
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
